
import React, { useCallback, useState, useEffect } from 'react';
// MUI
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Zoom from '@mui/material/Zoom';
import LoadingButton from '@mui/lab/LoadingButton';
import { TransitionProps } from '@mui/material/transitions';

// Components & Hooks
import { useSnackbar } from 'src/components/snackbar';
import Iconify from 'src/components/iconify';
import { Upload } from 'src/components/upload';
import { OptionModel } from 'src/types/api-response';
import { CaySam, CaySamNhatKy } from 'src/types/caysam';
import { authApiService } from 'src/api/auth-api';
import { getImageUrlWithCacheBuster } from 'src/utils/image-timestap';

import useCaySamFilterOptions from '../../../utils/use-caysam-filter-options';
import useGetCaySam from '../hooks/use-get-caysam';
import { useCaySam } from '../hooks/use-losam';
import NhatKyFormDialog from './nhat-ky-popup';

import CaySamInfo from './cay-sam-info';
import CaySamGrowthInfo from './cay-sam-growth-info';
import CaySamHistory from './cay-sam-history';

// --- PHẦN 1: Cấu hình hiệu ứng Zoom cho Popup ảnh ---
const Transition = React.forwardRef((
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) => <Zoom ref={ref} {...props} />);

type Props = {  
  open: boolean;
  onClose: () => void;
  title: string;
  lotName: string;
  // eslint-disable-next-line react/no-unused-prop-types
  lotCode: string;
  gardenName: string;
  positionLabel: string;
  caySamData: CaySam | null;
  onUpdateCaySam: (caySamData: Partial<CaySam>) => void;
  loaiTuoiOptions: OptionModel[];
  diemSucKhoeOptions?: OptionModel[];
  // eslint-disable-next-line react/no-unused-prop-types
  loading: boolean;
  onRefresh?: (newData: CaySam) => void;
};

export default function CaySamEditDrawer({
  open,
  onClose,
  title,
  lotName,
  gardenName,
  positionLabel,
  caySamData,
  onUpdateCaySam,
  loaiTuoiOptions,
  diemSucKhoeOptions,
  onRefresh,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { tinhTrangOptions, diemSucKhoeOptions: hookDiemOptions } = useCaySamFilterOptions();
  
  // Hook lấy API Upload file
  const { 
    getCaySamNhatKy, 
    caySamNhatKy, 
    loadingNhatKy, 
    errorNhatKy,
    addDinhKemFile,
    loadingAddFile
  } = useGetCaySam();

  const {
    addNhatKyByCaySamId,
    editNhatKyByCaySamId,
    loading: caySamLoading,
    error: caySamError,
  } = useCaySam();

  const mergedDiemOptions = diemSucKhoeOptions && diemSucKhoeOptions.length > 0 ? diemSucKhoeOptions : hookDiemOptions;

  const [openNhatKyDialog, setOpenNhatKyDialog] = useState(false);
  const [selectedNhatKy, setSelectedNhatKy] = useState<CaySamNhatKy | null>(null);
  const [isInvestor, setIsInvestor] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [showTimeline, setShowTimeline] = useState(false);

  // --- State quản lý xem ảnh popup ---
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // --- State quản lý Upload PDF ---
  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);

  // Check if user is investor
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await authApiService.getCurrentUser();
        const roles = (res.oneItem as any)?.htTaiKhoan?.htPhanQuyenTaiKhoans || [];
        const investor =
          Array.isArray(roles) && roles.some((r: any) => String(r?.maVaiTro) === 'nft_invester');
        if (mounted) setIsInvestor(investor);
      } catch (e) {
        if (mounted) setIsInvestor(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Load nhật ký khi drawer mở
  useEffect(() => {
    if (open && caySamData?.caySam_ID) {
      getCaySamNhatKy(String(caySamData.caySam_ID));
      setShowTimeline(false);
    }
  }, [open, caySamData?.caySam_ID, getCaySamNhatKy]);

  // Sync nhật ký từ hook vào caySamData
  useEffect(() => {
    if (caySamNhatKy && caySamNhatKy.length > 0) {
      onUpdateCaySam({ caySamNhatKys: caySamNhatKy });
    }
  }, [caySamNhatKy, onUpdateCaySam]);

  // Hiển thị lỗi
  useEffect(() => {
    if (errorNhatKy) {
      enqueueSnackbar(errorNhatKy, { variant: 'error' });
    }
  }, [errorNhatKy, enqueueSnackbar]);

  useEffect(() => {
    if (caySamError) {
      enqueueSnackbar(caySamError, { variant: 'error' });
    }
  }, [caySamError, enqueueSnackbar]);

  // Lấy nhật ký mới nhất
  const latestNhatKy = React.useMemo(() => {
    const list =
      caySamNhatKy?.filter((x): x is CaySamNhatKy => !!x) ||
      caySamData?.caySamNhatKys?.filter((x): x is CaySamNhatKy => !!x) ||
      [];

    if (!list.length) return null;

    return list.reduce((acc: CaySamNhatKy | null, cur: CaySamNhatKy) => {
      if (!acc) return cur;
      return new Date(cur.ngayGhi).getTime() > new Date(acc.ngayGhi).getTime() ? cur : acc;
    }, null as CaySamNhatKy | null);
  }, [caySamNhatKy, caySamData?.caySamNhatKys]);

  const imageUrlTongQuan = React.useMemo(
    () => getImageUrlWithCacheBuster(latestNhatKy?.hinhAnhTongQuan),
    [latestNhatKy?.hinhAnhTongQuan]
  );

  const imageUrlChiTiet = React.useMemo(
    () => getImageUrlWithCacheBuster(latestNhatKy?.hinhAnhChiTiet),
    [latestNhatKy?.hinhAnhChiTiet]
  );

  const isLatestNhatKyInCurrentMonth = React.useMemo(() => {
    if (!latestNhatKy?.ngayGhi) return false;
    const nhatKyDate = new Date(latestNhatKy.ngayGhi);
    const now = new Date();
    return (
      nhatKyDate.getFullYear() === now.getFullYear() && nhatKyDate.getMonth() === now.getMonth()
    );
  }, [latestNhatKy]);

  const base64ToFile = useCallback((base64: string, filename: string): File | null => {
    try {
      if (!base64.startsWith('data:')) return null;
      const arr = base64.split(',');
      const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
      const bstr = atob(arr[1]);
      const n = bstr.length;
      const u8arr = new Uint8Array(n);
      for (let i = 0; i < n; i += 1) {
        u8arr[i] = bstr.charCodeAt(i);
      }
      return new File([u8arr], filename, { type: mime });
    } catch (error) {
      console.error('Error converting base64 to file:', error);
      return null;
    }
  }, []);

  // --- LOGIC XỬ LÝ UPLOAD PDF ---
  
  const handleOpenPdfDialog = () => {
    setOpenPdfDialog(true);
  };

  const handleClosePdfDialog = () => {
    if (!loadingAddFile) {
        setOpenPdfDialog(false);
        handleDeletePdf(); 
    }
  };

  const handleDropPdf = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        enqueueSnackbar('Vui lòng chỉ chọn file PDF', { variant: 'warning' });
        return;
      }
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      setSelectedPdfFile(newFile);
    }
  }, [enqueueSnackbar]);

  const handleDeletePdf = () => {
    if (selectedPdfFile) {
        URL.revokeObjectURL((selectedPdfFile as any).preview);
    }
    setSelectedPdfFile(null);
  };

  const handleSubmitImport = async () => {
    if (selectedPdfFile && caySamData?.caySam_ID) {
      const success = await addDinhKemFile([String(caySamData.caySam_ID)], selectedPdfFile);
      
      if (success) {
        enqueueSnackbar('Đính kèm file PDF thành công!', { variant: 'success' });
        handleDeletePdf();
        setOpenPdfDialog(false); 
        if (onRefresh) {
            onRefresh(caySamData); 
        }
      } else {
        enqueueSnackbar('Có lỗi xảy ra khi upload file', { variant: 'error' });
      }
    }
  };
  // ------------------------------

  const handleNhatKySubmit = async (data: Partial<CaySamNhatKy>) => {
    try {
      if (!caySamData?.caySam_ID) {
        enqueueSnackbar('Không tìm thấy ID cây sâm', { variant: 'error' });
        return;
      }
      const soLaNumber = Number(data.soLa) || 0;
      const trongLuongNumber = Number(data.trongLuong) || 0
      const submitData = {
        id: data.id,
        CaySamId: String(caySamData.caySam_ID),
        NgayGhi: new Date().toISOString(),
        SoLa: soLaNumber || 0,
        DiemSucKhoe: data.diemSucKhoe || 0,
        TinhTrang: data.tinhTrang || 1,
        HinhAnh: null as boolean | null,
        IsThongBao: data.isThongBao || false,
        trongLuong: trongLuongNumber || 0, 
        GhiChu: data.ghiChu || '',
      };

      const files: File[] = [];
      if (data.hinhAnhTongQuan) {
        const tongQuanFile = base64ToFile(data.hinhAnhTongQuan, 'tongquan.jpg');
        if (tongQuanFile) {
          files.push(tongQuanFile);
          submitData.HinhAnh = true;
        }
      }
      if (data.hinhAnhChiTiet) {
        const chiTietFile = base64ToFile(data.hinhAnhChiTiet, 'chitiet.jpg');
        if (chiTietFile) {
          files.push(chiTietFile);
          submitData.HinhAnh = false;
        }
      }
      if (files.length === 0) {
        submitData.HinhAnh = null;
      }

      const isNewEntry = !data.id || data.id === '';
      const result = isNewEntry
        ? await addNhatKyByCaySamId(submitData, files)
        : await editNhatKyByCaySamId(submitData, files);

      if (result.messCode === 1) {
        enqueueSnackbar(isNewEntry ? 'Thêm nhật ký thành công' : 'Cập nhật nhật ký thành công', {
          variant: 'success',
        });
        if (caySamData.caySam_ID) {
          await getCaySamNhatKy(String(caySamData.caySam_ID));
          setRefreshKey((prev) => prev + 1);
          if (onRefresh) {
            const updatedCaySam: CaySam = {
              ...caySamData,
              caySamNhatKys: caySamNhatKy,
            } as CaySam;
            onRefresh(updatedCaySam);
          }
        }
        setOpenNhatKyDialog(false);
        setSelectedNhatKy(null);
      } else {
        enqueueSnackbar(result.message || 'Lỗi khi cập nhật nhật ký', { variant: 'error' });
      }
    } catch (error: any) {
      console.error('Error submitting nhat ky:', error);
      enqueueSnackbar(error?.message || 'Lỗi khi cập nhật nhật ký', { variant: 'error' });
    }
  };

  const handleAddNhatKy = () => {
    const newData: CaySamNhatKy = {
      id: '',
      caySamId: caySamData?.caySam_ID || '',
      taiKhoanId: '',
      tinhTrang: 1,
      diemSucKhoe: 0,
      soLa: 0,
      hinhAnhTongQuan: '',
      hinhAnhChiTiet: '',
      ngayGhi: new Date().toISOString(),
      ghiChu: '',
      isThongBao: false, 
      trongLuong: 0,
    };
    setSelectedNhatKy(newData);
    setOpenNhatKyDialog(true);
  };

  const handleEditNhatKy = () => {
    if (latestNhatKy && isLatestNhatKyInCurrentMonth) {
      setSelectedNhatKy(latestNhatKy);
      setOpenNhatKyDialog(true);
    }
  };

  const handleViewHistory = () => {
    if (caySamData?.caySam_ID) {
      getCaySamNhatKy(String(caySamData.caySam_ID));
      setShowTimeline(true);
    }
  };

  const handleOpenPreview = (url: string | null | undefined) => {
    if (url) setPreviewImage(url);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 800 } } }}
      >
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton onClick={onClose} sx={{ mr: 1 }}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1 }}>
              {title}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Box>
                <CaySamInfo
                    gardenName={gardenName}
                    lotName={lotName}
                    positionLabel={positionLabel}
                    caySamData={caySamData}
                    latestNhatKy={latestNhatKy}
                    loaiTuoiOptions={loaiTuoiOptions}
                    tinhTrangOptions={tinhTrangOptions}
                    diemSucKhoeOptions={mergedDiemOptions}
                />
            
                <CaySamGrowthInfo
                    latestNhatKy={latestNhatKy}
                    imageUrlTongQuan={imageUrlTongQuan}
                    imageUrlChiTiet={imageUrlChiTiet}
                    caySamData={caySamData}
                    isInvestor={isInvestor}
                    isLatestNhatKyInCurrentMonth={isLatestNhatKyInCurrentMonth}
                    refreshKey={refreshKey}
                    onAddNhatKy={handleAddNhatKy}
                    onEditNhatKy={handleEditNhatKy}
                    onOpenPreview={handleOpenPreview}
                    onOpenPdfDialog={handleOpenPdfDialog}
                />

                <Divider sx={{ my: 3 }} />

                <CaySamHistory
                    caySamData={caySamData}
                    caySamNhatKy={caySamNhatKy}
                    loadingNhatKy={loadingNhatKy}
                    showTimeline={showTimeline}
                    onViewHistory={handleViewHistory}
                    tinhTrangOptions={tinhTrangOptions}
                    onOpenPreview={handleOpenPreview}
                />
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button color='error' variant="contained" onClick={onClose} fullWidth>
              Đóng
            </Button> 
          </Box>
        </Box>
      </Drawer>

      {/* Dialog nhật ký */}
      <NhatKyFormDialog
        open={openNhatKyDialog}
        onClose={() => {
          setOpenNhatKyDialog(false);
          setSelectedNhatKy(null);
        }}
        onSubmit={handleNhatKySubmit}
        nhatKyData={selectedNhatKy}
        tinhTrangOptions={tinhTrangOptions}
        diemSucKhoeOptions={mergedDiemOptions}
        loading={caySamLoading}
      />

      {/* Render Dialog xem ảnh */}
      <ImagePreviewDialog
        open={Boolean(previewImage)}
        imageUrl={previewImage}
        onClose={() => setPreviewImage(null)}
      />

      {/* --- DIALOG UPLOAD PDF --- */}
      <Dialog 
        open={openPdfDialog} 
        onClose={handleClosePdfDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2 }}>
            Import dữ liệu từ PDF
            <IconButton onClick={handleClosePdfDialog} size="small" disabled={loadingAddFile}>
                <Iconify icon="eva:close-fill" />
            </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
             {!selectedPdfFile && (
                <Upload
                    file={selectedPdfFile}
                    onDrop={handleDropPdf}
                    onDelete={handleDeletePdf}
                    helperTitle="Kéo thả hoặc chọn file PDF"
                    thumbnail={false}
                    accept={{ 'application/pdf': [] }}
                    helperText={
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center', color: 'text.disabled' }}>
                            *.pdf (Bắt buộc)
                        </Typography>
                    }
                    sx={{ '& .upload-zone': { height: 200, border: '2px dashed', borderColor: 'grey.300', borderRadius: 1 }}}
                />
            )}

            {selectedPdfFile && ( 
                <Box sx={{ position: 'relative', width: '100%', height: '60vh', bgcolor: 'background.neutral', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: 10, right: 20, zIndex: 9, bgcolor: 'rgba(255, 255, 255, 0.9)', borderRadius: '50%', boxShadow: 2 }}>
                        <IconButton color="error" onClick={handleDeletePdf} disabled={loadingAddFile}>
                            <Iconify icon="eva:trash-2-fill" />
                        </IconButton>
                    </Box>
                    <embed src={(selectedPdfFile as any).preview} type="application/pdf" width="100%" height="100%" style={{ border: 'none' }} />
                </Box>
            )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClosePdfDialog} color="inherit" disabled={loadingAddFile}>
                Hủy bỏ
            </Button>
            
            <LoadingButton 
                onClick={handleSubmitImport} 
                variant="contained" 
                loading={loadingAddFile}
                disabled={!selectedPdfFile}
                startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
            >
                Xử lý File
            </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

// --- Component ImagePreviewDialog ---
type ImagePreviewProps = {
  open: boolean;
  imageUrl: string | null;
  onClose: () => void;
};

function ImagePreviewDialog({ open, imageUrl, onClose }: ImagePreviewProps) {
  if (!imageUrl) return null;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.9)' },
        },
      }}
    >
      <Box
        sx={{
          position: 'fixed',
          top: 24,
          right: 24,
          zIndex: 9999,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.4)' },
          }}
        >
          <Iconify icon="eva:close-fill" width={32} />
        </IconButton>
      </Box>

      <Box
        component="img"
        src={imageUrl}
        alt="Preview"
        onClick={onClose}
        sx={{
          maxHeight: '90vh',
          maxWidth: '90vw',
          objectFit: 'contain',
          borderRadius: 2,
          cursor: 'zoom-out', 
          boxShadow: 24,
        }}
      />
    </Dialog>
  );
}